import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Mail, X, ShieldCheck } from "lucide-react";
import api from "../../lib/api";

/* Circle management: create circles, invite by email, revoke access. */
const ROLES = [
  ["trusted_viewer", "Trusted Viewer"], ["guardian", "Guardian"],
  ["emergency_contact", "Emergency Contact"], ["trip_coordinator", "Trip Coordinator"],
];

export default function GuardianCircles() {
  const [circles, setCircles] = useState(null);
  const [name, setName] = useState("");
  const [invite, setInvite] = useState(null); // circle id being invited to
  const [inv, setInv] = useState({ email: "", name: "", role: "trusted_viewer", access_days: 30, precision_cap: "exact" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/guardian/circles").then((r) => setCircles(r.data.items)).catch(() => setCircles([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/guardian/circles", { name }); setName(""); toast.success("Circle created"); load(); }
    catch { toast.error("Could not create circle"); }
  };

  const sendInvite = async () => {
    if (!inv.email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post(`/guardian/circles/${invite}/invite`, { ...inv, access_days: Number(inv.access_days) });
      toast.success("Invitation emailed"); setInvite(null); setInv({ ...inv, email: "", name: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
    setBusy(false);
  };

  const revoke = async (cid, mid) => {
    try { await api.post(`/guardian/circles/${cid}/members/${mid}/revoke`); toast.success("Access revoked immediately"); load(); }
    catch { toast.error("Could not revoke"); }
  };

  if (!circles) return <p className="text-sm text-zinc-400">Loading…</p>;

  return (
    <div data-testid="guardian-circles">
      <div className="flex gap-2 mb-5">
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="guardian-circle-name-input"
          placeholder="New circle name (e.g. Family, Trip to Kochi)…"
          className="flex-1 border border-zinc-300 rounded-xl px-3 py-2.5 text-sm" />
        <button onClick={create} data-testid="guardian-circle-create-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-sm font-800 rounded-xl px-4 py-2.5 hover:bg-emerald-700"><Plus className="w-4 h-4" /> Create</button>
      </div>

      {circles.length === 0 && (
        <p className="text-sm text-zinc-400 border border-dashed border-zinc-300 rounded-xl px-4 py-8 text-center" data-testid="guardian-circles-empty">
          No circles yet. A circle is the private group of people allowed to follow you — family, friends, a pickup group.
        </p>
      )}

      {circles.map((c) => (
        <div key={c.id} className="border border-zinc-200 bg-white rounded-2xl p-4 mb-4" data-testid={`guardian-circle-${c.id}`}>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-600" />
            <span className="font-800 text-sm">{c.name}</span>
            <span className="text-[11px] text-zinc-400">{(c.members || []).length} member{(c.members || []).length !== 1 && "s"}</span>
            <button onClick={() => { setInvite(invite === c.id ? null : c.id); }} data-testid={`guardian-invite-btn-${c.id}`}
              className="ml-auto inline-flex items-center gap-1 text-xs font-800 text-blue-700 hover:underline"><Mail className="w-3.5 h-3.5" /> Invite</button>
          </div>

          {(c.members || []).length > 0 && (
            <div className="mt-3 space-y-1.5">
              {c.members.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-zinc-50 pb-1.5" data-testid="guardian-member-row">
                  <span className="font-700">{m.name}</span>
                  <span className="text-zinc-400">{m.email}</span>
                  <span className="bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5 text-[10px] font-700">{ROLES.find(([r]) => r === m.role)?.[1]}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-800 uppercase ${m.status === "accepted" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{m.status}</span>
                  <span className="text-[10px] text-zinc-400">max precision: {m.permissions?.precision_cap}</span>
                  <span className="text-[10px] text-zinc-400">until {new Date(m.access_expires_at).toLocaleDateString()}</span>
                  <button onClick={() => revoke(c.id, m.id)} data-testid={`guardian-revoke-${m.id}`}
                    className="ml-auto inline-flex items-center gap-1 text-[10px] font-800 text-rose-600 hover:underline"><X className="w-3 h-3" /> Revoke now</button>
                </div>
              ))}
            </div>
          )}

          {invite === c.id && (
            <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-xl p-3 space-y-2" data-testid="guardian-invite-form">
              <div className="grid sm:grid-cols-2 gap-2">
                <input value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} data-testid="guardian-invite-email"
                  placeholder="Their email" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
                <input value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} data-testid="guardian-invite-name"
                  placeholder="Their name (optional)" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
                <input value={inv.phone || ""} onChange={(e) => setInv({ ...inv, phone: e.target.value })} data-testid="guardian-invite-phone"
                  placeholder="Mobile for SMS alerts, e.g. +9198… (optional)" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm sm:col-span-2" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <select value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })} data-testid="guardian-invite-role" className="border border-zinc-300 rounded-lg px-2 py-2 text-xs bg-white">
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <select value={inv.precision_cap} onChange={(e) => setInv({ ...inv, precision_cap: e.target.value })} data-testid="guardian-invite-precision" className="border border-zinc-300 rounded-lg px-2 py-2 text-xs bg-white">
                  <option value="exact">May see exact GPS</option><option value="approx">Max: approximate</option>
                  <option value="city">Max: city-level</option><option value="status">Status only</option>
                </select>
                <select value={inv.access_days} onChange={(e) => setInv({ ...inv, access_days: e.target.value })} data-testid="guardian-invite-days" className="border border-zinc-300 rounded-lg px-2 py-2 text-xs bg-white">
                  {[[7, "7 days"], [14, "14 days"], [30, "30 days"], [90, "90 days"], [365, "1 year"]].map(([v, l]) => <option key={v} value={v}>Access: {l}</option>)}
                </select>
              </div>
              <button onClick={sendInvite} disabled={busy} data-testid="guardian-invite-send"
                className="inline-flex items-center gap-1.5 bg-blue-700 text-white text-xs font-800 rounded-full px-4 py-2 hover:bg-blue-800 disabled:opacity-50"><Mail className="w-3.5 h-3.5" /> Send invitation</button>
            </div>
          )}
        </div>
      ))}

      <p className="mt-2 text-[11px] text-zinc-400 flex items-start gap-1.5"><ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-500" />
        Invitations must be accepted before any access begins. Access expires automatically and can be revoked instantly — the revoked person is never notified.</p>
    </div>
  );
}
